import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SEO } from '../components/common';
import { CONTACT_INFO } from '../constants';
import './AdminDashboard.css';

interface Booking {
	id: string;
	name: string;
	email: string;
	phone: string;
	date: string;
	time: string;
	guestCount: number;
	region: string;
	message?: string;
	formType: 'booking' | 'estimate';
	status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
	couponCode?: string;
	couponDiscount?: string;
	referralSource?: string;
	createdAt: string;
}

interface Coupon {
	id: string;
	code: string;
	type: 'percentage' | 'fixed';
	value: number;
	minGuests: number;
	maxUses: number;
	usedCount: number;
	validFrom: string;
	validUntil: string;
	enabled: boolean;
	createdAt: string;
}

interface BlockedDate {
	date: string;
	reason?: string;
}

interface Review {
	id: string;
	name: string;
	location: string;
	rating: number;
	review: string;
	event: string;
	createdAt: string;
	visible: boolean;
}

interface TimeSlot {
	id: string;
	label: string;
	startTime: string;
	endTime: string;
	enabled: boolean;
}

interface SocialLinks {
	instagram: string;
	facebook: string;
	tiktok: string;
}

interface PromoBanner {
	enabled: boolean;
	text: string;
	emoji: string;
}

interface ContactInfo {
	phone: string;
	email: string;
	contactPerson: string;
}

interface GalleryImage {
	id: string;
	url: string;
	caption: string;
	order: number;
}

interface FeatureToggles {
	photoShare: boolean;
	referralProgram: boolean;
	newsletter: boolean;
	specialOffer: boolean;
	instagramFeed: boolean;
	coupons: boolean;
}

interface AppSettings {
	timeSlots: TimeSlot[];
	minGuests: number;
	maxGuests: number;
	pricePerPerson: number;
	minimumOrder: number;
	socialLinks: SocialLinks;
	promoBanner: PromoBanner;
	contactInfo: ContactInfo;
	galleryImages: GalleryImage[];
	featureToggles: FeatureToggles;
}

interface InstagramPost {
	id: string;
	image: string; // base64 图片
	link: string;  // Instagram 帖子链接
	caption?: string;
}

interface InstagramSettings {
	handle: string;
	posts: InstagramPost[];
	updatedAt: string;
}

type MenuType = 'dashboard' | 'bookings' | 'calendar' | 'reviews' | 'coupons' | 'instagram' | 'gallery' | 'settings';
type StatusType = 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled';

const DEFAULT_FEATURE_TOGGLES: FeatureToggles = {
	photoShare: true,
	referralProgram: true,
	newsletter: true,
	specialOffer: true,
	instagramFeed: true,
	coupons: true,
};

const DEFAULT_SETTINGS: AppSettings = {
	timeSlots: [
		{ id: 'afternoon', label: 'Afternoon', startTime: '13:00', endTime: '15:00', enabled: true },
		{ id: 'evening', label: 'Evening', startTime: '17:00', endTime: '19:00', enabled: true },
		{ id: 'night', label: 'Night', startTime: '19:00', endTime: '21:00', enabled: true },
	],
	minGuests: 10,
	maxGuests: 100,
	pricePerPerson: 60,
	minimumOrder: 600,
	socialLinks: {
		instagram: 'https://instagram.com/happyhibachi',
		facebook: 'https://facebook.com/happyhibachi',
		tiktok: 'https://tiktok.com/@happyhibachi',
	},
	promoBanner: {
		enabled: true,
		text: 'Book Today & Get $30 OFF!',
		emoji: '🔥',
	},
	contactInfo: {
		phone: '909-615-6633',
		email: 'happyhibachi@gmail.com',
		contactPerson: 'Happy Hibachi Team',
	},
	galleryImages: [],
	featureToggles: DEFAULT_FEATURE_TOGGLES,
};

const AdminDashboard: React.FC = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [password, setPassword] = useState('');
	const [loginError, setLoginError] = useState('');
	const [bookings, setBookings] = useState<Booking[]>([]);
	const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
	const [reviews, setReviews] = useState<Review[]>([]);
	const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
	const [loading, setLoading] = useState(false);
	const [activeMenu, setActiveMenu] = useState<MenuType>('dashboard');
	const [statusFilter, setStatusFilter] = useState<StatusType>('all');
	const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [updating, setUpdating] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	
	// 日历状态
	const [calendarMonth, setCalendarMonth] = useState(new Date());
	const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
	const [blockReason, setBlockReason] = useState('');
	
	// 提醒状态
	const [reminderStatus, setReminderStatus] = useState<{
		pending: number;
		reminded: number;
		loading: boolean;
	}>({ pending: 0, reminded: 0, loading: false });

	// Instagram 状态
	const [instagramSettings, setInstagramSettings] = useState<InstagramSettings>({
		handle: '',
		posts: [],
		updatedAt: '',
	});
	const [instagramLoading, setInstagramLoading] = useState(false);
	const [newPostLink, setNewPostLink] = useState('');
	const [newPostImage, setNewPostImage] = useState<string | null>(null);

	// 优惠码状态
	const [coupons, setCoupons] = useState<Coupon[]>([]);
	const [showCouponForm, setShowCouponForm] = useState(false);
	const [newCoupon, setNewCoupon] = useState<Partial<Coupon>>({
		code: '',
		type: 'percentage',
		value: 10,
		minGuests: 0,
		maxUses: 0,
		validFrom: new Date().toISOString().split('T')[0],
		validUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
		enabled: true,
	});

	// Toast
	const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
		setToast({ message, type });
		setTimeout(() => setToast(null), 3000);
	};

	// 检查登录状态
	useEffect(() => {
		const token = sessionStorage.getItem('admin_token');
		if (token) {
			setIsAuthenticated(true);
			fetchBookings();
			fetchBlockedDates();
			fetchSettings();
			fetchReminderStatus();
			fetchReviews();
			fetchInstagramSettings();
			fetchCoupons();
		}
	}, []);

	// 登录
	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoginError('');
		setLoading(true);

		try {
			const response = await fetch('/api/admin/login', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ password }),
			});
			const result = await response.json();
			if (result.success) {
				sessionStorage.setItem('admin_token', result.token);
				setIsAuthenticated(true);
				fetchBookings();
				fetchBlockedDates();
				fetchSettings();
			} else {
				setLoginError('密码错误');
			}
		} catch {
			setLoginError('登录失败，请重试');
		}
		setLoading(false);
	};

	// 登出
	const handleLogout = () => {
		sessionStorage.removeItem('admin_token');
		setIsAuthenticated(false);
		setBookings([]);
	};

	// 获取预约
	const fetchBookings = useCallback(async () => {
		setLoading(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/admin/bookings', {
				headers: { Authorization: `Bearer ${token}` },
			});
			const result = await response.json();
			if (result.success) {
				setBookings(result.bookings || []);
			}
		} catch (error) {
			console.error('Failed to fetch bookings:', error);
			showToast('获取预约失败', 'error');
		}
		setLoading(false);
	}, []);

	// 获取不可用日期
	const fetchBlockedDates = useCallback(async () => {
		try {
			const response = await fetch('/api/calendar');
			const result = await response.json();
			if (result.success) {
				setBlockedDates(result.blockedDates || []);
			}
		} catch (error) {
			console.error('Failed to fetch blocked dates:', error);
		}
	}, []);

	// 获取设置
	const fetchSettings = useCallback(async () => {
		try {
			const response = await fetch('/api/settings');
			const result = await response.json();
			if (result.success && result.settings) {
				setSettings(result.settings);
			}
		} catch (error) {
			console.error('Failed to fetch settings:', error);
		}
	}, []);

	// 保存设置
	const saveSettings = async (newSettings: Partial<AppSettings>) => {
		setUpdating(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/settings', {
				method: 'POST',
				headers: { 
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(newSettings),
			});
			const result = await response.json();
			if (result.success) {
				setSettings(result.settings);
				showToast('设置已保存');
			} else {
				showToast('保存失败', 'error');
			}
		} catch (error) {
			console.error('Failed to save settings:', error);
			showToast('保存失败', 'error');
		}
		setUpdating(false);
	};

	// 切换时间段启用状态
	const toggleTimeSlot = (slotId: string) => {
		const newTimeSlots = settings.timeSlots.map(slot =>
			slot.id === slotId ? { ...slot, enabled: !slot.enabled } : slot
		);
		saveSettings({ timeSlots: newTimeSlots });
	};

	// 更新时间段时间
	const updateTimeSlotTime = (slotId: string, field: 'startTime' | 'endTime', value: string) => {
		const newTimeSlots = settings.timeSlots.map(slot =>
			slot.id === slotId ? { ...slot, [field]: value } : slot
		);
		setSettings(prev => ({ ...prev, timeSlots: newTimeSlots }));
	};

	// 添加新时间段
	const addTimeSlot = () => {
		const newSlot: TimeSlot = {
			id: `custom_${Date.now()}`,
			label: '新时段',
			startTime: '12:00',
			endTime: '14:00',
			enabled: true,
		};
		const newTimeSlots = [...settings.timeSlots, newSlot];
		saveSettings({ timeSlots: newTimeSlots });
	};

	// 删除时间段
	const deleteTimeSlot = (slotId: string) => {
		const newTimeSlots = settings.timeSlots.filter(slot => slot.id !== slotId);
		saveSettings({ timeSlots: newTimeSlots });
	};

	// 更新时间段标签
	const updateTimeSlotLabel = (slotId: string, label: string) => {
		const newTimeSlots = settings.timeSlots.map(slot =>
			slot.id === slotId ? { ...slot, label } : slot
		);
		setSettings(prev => ({ ...prev, timeSlots: newTimeSlots }));
	};

	// 上传图库图片
	const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		const newImages: GalleryImage[] = [];
		const currentImages = settings.galleryImages || [];
		const maxOrder = currentImages.length > 0 
			? Math.max(...currentImages.map(img => img.order)) 
			: 0;

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (file.size > 5 * 1024 * 1024) {
				showToast(`${file.name} 超过 5MB 限制`, 'error');
				continue;
			}

			try {
				const base64 = await new Promise<string>((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result as string);
					reader.onerror = reject;
					reader.readAsDataURL(file);
				});

				newImages.push({
					id: `img_${Date.now()}_${i}`,
					url: base64,
					caption: '',
					order: maxOrder + i + 1,
				});
			} catch (error) {
				console.error('Failed to read file:', error);
			}
		}

		if (newImages.length > 0) {
			const updatedImages = [...currentImages, ...newImages];
			await saveSettings({ galleryImages: updatedImages });
			showToast(`已上传 ${newImages.length} 张图片`);
		}

		// 重置 input
		e.target.value = '';
	};

	// 更新图片说明
	const updateGalleryCaption = (imageId: string, caption: string) => {
		const updatedImages = (settings.galleryImages || []).map(img =>
			img.id === imageId ? { ...img, caption } : img
		);
		setSettings(prev => ({ ...prev, galleryImages: updatedImages }));
	};

	// 移动图片顺序
	const moveGalleryImage = (imageId: string, direction: 'up' | 'down') => {
		const images = [...(settings.galleryImages || [])].sort((a, b) => a.order - b.order);
		const index = images.findIndex(img => img.id === imageId);
		if (index === -1) return;

		const targetIndex = direction === 'up' ? index - 1 : index + 1;
		if (targetIndex < 0 || targetIndex >= images.length) return;

		// 交换 order
		const temp = images[index].order;
		images[index].order = images[targetIndex].order;
		images[targetIndex].order = temp;

		saveSettings({ galleryImages: images });
	};

	// 删除图片
	const deleteGalleryImage = (imageId: string) => {
		if (!window.confirm('确定要删除这张图片吗？')) return;
		const updatedImages = (settings.galleryImages || []).filter(img => img.id !== imageId);
		saveSettings({ galleryImages: updatedImages });
		showToast('图片已删除');
	};

	// 获取提醒状态
	const fetchReminderStatus = useCallback(async () => {
		try {
			const response = await fetch('/api/cron/reminders');
			const result = await response.json();
			if (result.success) {
				setReminderStatus(prev => ({
					...prev,
					pending: result.pendingReminders || 0,
					reminded: result.alreadyReminded || 0,
				}));
			}
		} catch (error) {
			console.error('Failed to fetch reminder status:', error);
		}
	}, []);

	// 发送提醒邮件
	const sendReminders = async () => {
		setReminderStatus(prev => ({ ...prev, loading: true }));
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/cron/reminders', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
			});
			const result = await response.json();
			if (result.success) {
				showToast(`已发送 ${result.processed} 封提醒邮件`);
				fetchReminderStatus();
			} else {
				showToast('发送失败', 'error');
			}
		} catch (error) {
			console.error('Failed to send reminders:', error);
			showToast('发送失败', 'error');
		}
		setReminderStatus(prev => ({ ...prev, loading: false }));
	};

	// 导出预约数据为 CSV
	const exportBookingsCSV = () => {
		if (bookings.length === 0) {
			showToast('没有可导出的数据', 'error');
			return;
		}

		// CSV 列头
		const headers = ['ID', '姓名', '邮箱', '电话', '日期', '时间', '人数', '地区', '状态', '类型', '优惠码', '折扣', '推荐来源', '留言', '创建时间'];
		
		// CSV 内容
		const csvContent = [
			headers.join(','),
			...bookings.map(b => [
				b.id,
				`"${b.name.replace(/"/g, '""')}"`,
				b.email,
				b.phone,
				b.date,
				b.time,
				b.guestCount,
				b.region,
				b.status,
				b.formType,
				b.couponCode || '',
				b.couponDiscount || '',
				b.referralSource || '',
				`"${(b.message || '').replace(/"/g, '""')}"`,
				b.createdAt,
			].join(','))
		].join('\n');

		// 创建下载
		const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.setAttribute('href', url);
		link.setAttribute('download', `bookings_${new Date().toISOString().split('T')[0]}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		
		showToast(`已导出 ${bookings.length} 条记录`);
	};

	// 复制预约数据到剪贴板
	const copyBookingsToClipboard = async () => {
		if (bookings.length === 0) {
			showToast('没有可复制的数据', 'error');
			return;
		}

		const statusMap: Record<string, string> = {
			pending: '待确认',
			confirmed: '已确认',
			completed: '已完成',
			cancelled: '已取消',
		};

		const textContent = bookings.map((b, i) => 
			`【预约 ${i + 1}】\n` +
			`姓名: ${b.name}\n` +
			`电话: ${b.phone}\n` +
			`邮箱: ${b.email}\n` +
			`日期: ${b.date}\n` +
			`时间: ${b.time}\n` +
			`人数: ${b.guestCount}人\n` +
			`地区: ${b.region}\n` +
			`状态: ${statusMap[b.status] || b.status}\n` +
			(b.couponCode ? `优惠码: ${b.couponCode} (${b.couponDiscount})\n` : '') +
			(b.message ? `留言: ${b.message}\n` : '') +
			`创建时间: ${new Date(b.createdAt).toLocaleString('zh-CN')}`
		).join('\n\n' + '─'.repeat(30) + '\n\n');

		const header = `Happy Hibachi 预约数据导出\n导出时间: ${new Date().toLocaleString('zh-CN')}\n总计: ${bookings.length} 条记录\n\n${'═'.repeat(30)}\n\n`;

		try {
			await navigator.clipboard.writeText(header + textContent);
			showToast(`已复制 ${bookings.length} 条记录到剪贴板`);
		} catch (error) {
			console.error('Copy failed:', error);
			showToast('复制失败，请重试', 'error');
		}
	};

	// 导出预约数据为图片
	const exportBookingsAsImage = async () => {
		if (bookings.length === 0) {
			showToast('没有可导出的数据', 'error');
			return;
		}

		const statusMap: Record<string, string> = {
			pending: '⏳ 待确认',
			confirmed: '✅ 已确认',
			completed: '🎉 已完成',
			cancelled: '❌ 已取消',
		};

		// 创建 canvas
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			showToast('浏览器不支持此功能', 'error');
			return;
		}

		// 计算尺寸
		const padding = 40;
		const rowHeight = 28;
		const headerHeight = 80;
		const itemPadding = 20;
		const itemsPerRow = 1;
		const itemHeight = 220;
		const cols = Math.min(2, Math.ceil(bookings.length / 5));
		const rows = Math.ceil(bookings.length / cols);
		
		canvas.width = Math.max(800, cols * 380 + padding * 2);
		canvas.height = headerHeight + rows * itemHeight + padding * 2;

		// 背景
		ctx.fillStyle = '#0f0f0f';
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		// 标题
		ctx.fillStyle = '#FF6B35';
		ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
		ctx.fillText('🍱 Happy Hibachi 预约数据', padding, padding + 30);
		
		ctx.fillStyle = '#888';
		ctx.font = '14px system-ui, -apple-system, sans-serif';
		ctx.fillText(`导出时间: ${new Date().toLocaleString('zh-CN')} | 共 ${bookings.length} 条记录`, padding, padding + 55);

		// 绘制预约卡片
		bookings.forEach((b, i) => {
			const col = i % cols;
			const row = Math.floor(i / cols);
			const x = padding + col * 380;
			const y = headerHeight + padding + row * itemHeight;

			// 卡片背景
			ctx.fillStyle = '#1a1a1a';
			ctx.beginPath();
			ctx.roundRect(x, y, 360, itemHeight - 20, 8);
			ctx.fill();

			// 卡片边框
			ctx.strokeStyle = '#333';
			ctx.lineWidth = 1;
			ctx.stroke();

			// 内容
			ctx.fillStyle = '#fff';
			ctx.font = 'bold 16px system-ui, -apple-system, sans-serif';
			ctx.fillText(`${b.name}`, x + itemPadding, y + 30);

			ctx.fillStyle = '#FF6B35';
			ctx.font = '12px system-ui, -apple-system, sans-serif';
			ctx.fillText(statusMap[b.status] || b.status, x + 280, y + 30);

			ctx.fillStyle = '#aaa';
			ctx.font = '13px system-ui, -apple-system, sans-serif';
			const lines = [
				`📞 ${b.phone}`,
				`📧 ${b.email}`,
				`📅 ${b.date} ${b.time}`,
				`👥 ${b.guestCount}人 | 📍 ${b.region}`,
				b.couponCode ? `🎫 ${b.couponCode}` : '',
			].filter(Boolean);

			lines.forEach((line, li) => {
				ctx.fillText(line, x + itemPadding, y + 60 + li * 26);
			});
		});

		// 下载
		canvas.toBlob((blob) => {
			if (!blob) {
				showToast('生成图片失败', 'error');
				return;
			}
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.download = `bookings_${new Date().toISOString().split('T')[0]}.png`;
			link.href = url;
			link.click();
			URL.revokeObjectURL(url);
			showToast(`已保存图片 (${bookings.length} 条记录)`);
		}, 'image/png');
	};

	// 获取评价
	const fetchReviews = useCallback(async () => {
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/reviews', {
				headers: { Authorization: `Bearer ${token}` },
			});
			const result = await response.json();
			if (result.success) {
				setReviews(result.reviews || []);
			}
		} catch (error) {
			console.error('Failed to fetch reviews:', error);
		}
	}, []);

	// 添加评价
	const addReview = async (reviewData: Omit<Review, 'id' | 'createdAt'>) => {
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/reviews', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(reviewData),
			});
			const result = await response.json();
			if (result.success) {
				showToast('评价已添加');
				fetchReviews();
				return true;
			}
		} catch (error) {
			console.error('Failed to add review:', error);
		}
		showToast('添加失败', 'error');
		return false;
	};

	// 切换评价可见性
	const toggleReviewVisibility = async (id: string, visible: boolean) => {
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/reviews', {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ id, visible }),
			});
			const result = await response.json();
			if (result.success) {
				setReviews(prev => prev.map(r => r.id === id ? { ...r, visible } : r));
				showToast(visible ? '评价已显示' : '评价已隐藏');
			}
		} catch (error) {
			console.error('Failed to toggle review:', error);
			showToast('操作失败', 'error');
		}
	};

	// 删除评价
	const deleteReview = async (id: string) => {
		if (!confirm('确定删除此评价？')) return;
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/reviews', {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ id }),
			});
			const result = await response.json();
			if (result.success) {
				setReviews(prev => prev.filter(r => r.id !== id));
				showToast('评价已删除');
			}
		} catch (error) {
			console.error('Failed to delete review:', error);
			showToast('删除失败', 'error');
		}
	};

	// 获取 Instagram 设置
	const fetchInstagramSettings = useCallback(async () => {
		try {
			const response = await fetch('/api/instagram');
			const result = await response.json();
			if (result.success && result.settings) {
				setInstagramSettings(result.settings);
			}
		} catch (error) {
			console.error('Failed to fetch Instagram settings:', error);
		}
	}, []);

	// 保存 Instagram handle
	const saveInstagramHandle = async (handle: string) => {
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/instagram', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ handle }),
			});
			const result = await response.json();
			if (result.success) {
				setInstagramSettings(result.settings);
				showToast('账号已更新');
			}
		} catch (error) {
			console.error('Failed to save Instagram handle:', error);
			showToast('保存失败', 'error');
		}
	};

	// 处理图片上传
	const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		
		// 检查文件大小 (最大 2MB)
		if (file.size > 2 * 1024 * 1024) {
			showToast('图片不能超过 2MB', 'error');
			return;
		}
		
		const reader = new FileReader();
		reader.onload = (event) => {
			setNewPostImage(event.target?.result as string);
		};
		reader.readAsDataURL(file);
	};

	// 添加新帖子
	const addInstagramPost = async () => {
		if (!newPostImage || !newPostLink.trim()) {
			showToast('请上传图片并填写链接', 'error');
			return;
		}
		
		setInstagramLoading(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/instagram', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({
					action: 'add',
					post: {
						image: newPostImage,
						link: newPostLink.trim(),
					},
				}),
			});
			const result = await response.json();
			if (result.success) {
				setInstagramSettings(result.settings);
				setNewPostImage(null);
				setNewPostLink('');
				showToast('帖子已添加');
			} else {
				showToast('添加失败', 'error');
			}
		} catch (error) {
			console.error('Failed to add Instagram post:', error);
			showToast('添加失败', 'error');
		}
		setInstagramLoading(false);
	};

	// 删除帖子
	const deleteInstagramPost = async (postId: string) => {
		if (!confirm('确定删除此帖子？')) return;
		
		setInstagramLoading(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/instagram', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({
					action: 'delete',
					postId,
				}),
			});
			const result = await response.json();
			if (result.success) {
				setInstagramSettings(result.settings);
				showToast('帖子已删除');
			} else {
				showToast('删除失败', 'error');
			}
		} catch (error) {
			console.error('Failed to delete Instagram post:', error);
			showToast('删除失败', 'error');
		}
		setInstagramLoading(false);
	};

	// 移动帖子顺序
	const moveInstagramPost = async (postId: string, direction: 'up' | 'down') => {
		const posts = [...instagramSettings.posts];
		const index = posts.findIndex(p => p.id === postId);
		if (index === -1) return;
		
		const newIndex = direction === 'up' ? index - 1 : index + 1;
		if (newIndex < 0 || newIndex >= posts.length) return;
		
		// 交换位置
		[posts[index], posts[newIndex]] = [posts[newIndex], posts[index]];
		
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/instagram', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ posts }),
			});
			const result = await response.json();
			if (result.success) {
				setInstagramSettings(result.settings);
			}
		} catch (error) {
			console.error('Failed to reorder posts:', error);
		}
	};

	// 获取优惠码
	const fetchCoupons = useCallback(async () => {
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/coupons', {
				headers: { Authorization: `Bearer ${token}` },
			});
			const result = await response.json();
			if (result.success) {
				setCoupons(result.coupons || []);
			}
		} catch (error) {
			console.error('Failed to fetch coupons:', error);
		}
	}, []);

	// 创建优惠码
	const createCoupon = async () => {
		if (!newCoupon.code) {
			showToast('请输入优惠码', 'error');
			return;
		}
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/coupons', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(newCoupon),
			});
			const result = await response.json();
			if (result.success) {
				showToast('优惠码已创建');
				fetchCoupons();
				setShowCouponForm(false);
				setNewCoupon({
					code: '',
					type: 'percentage',
					value: 10,
					minGuests: 0,
					maxUses: 0,
					validFrom: new Date().toISOString().split('T')[0],
					validUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
					enabled: true,
				});
			} else {
				showToast(result.error || '创建失败', 'error');
			}
		} catch (error) {
			console.error('Failed to create coupon:', error);
			showToast('创建失败', 'error');
		}
	};

	// 切换优惠码状态
	const toggleCoupon = async (id: string, enabled: boolean) => {
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/coupons', {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ id, enabled }),
			});
			const result = await response.json();
			if (result.success) {
				setCoupons(prev => prev.map(c => c.id === id ? { ...c, enabled } : c));
				showToast(enabled ? '优惠码已启用' : '优惠码已停用');
			}
		} catch (error) {
			console.error('Failed to toggle coupon:', error);
		}
	};

	// 删除优惠码
	const deleteCoupon = async (id: string) => {
		if (!confirm('确定删除此优惠码？')) return;
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/coupons', {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ id }),
			});
			const result = await response.json();
			if (result.success) {
				setCoupons(prev => prev.filter(c => c.id !== id));
				showToast('优惠码已删除');
			}
		} catch (error) {
			console.error('Failed to delete coupon:', error);
		}
	};

	// 计算推荐来源统计
	const referralStats = useMemo(() => {
		const stats: Record<string, number> = {};
		bookings.forEach(b => {
			const source = b.referralSource || '直接访问';
			stats[source] = (stats[source] || 0) + 1;
		});
		return Object.entries(stats)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 10);
	}, [bookings]);

	// 更新状态
	const updateBookingStatus = async (id: string, status: Booking['status']) => {
		setUpdating(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/admin/bookings', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
				body: JSON.stringify({ id, status }),
			});
			const result = await response.json();
			if (result.success) {
				setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
				if (selectedBooking?.id === id) setSelectedBooking({ ...selectedBooking, status });
				showToast('状态更新成功');
			}
		} catch {
			showToast('更新失败', 'error');
		}
		setUpdating(false);
	};

	// 删除预约
	const deleteBooking = async (id: string) => {
		setUpdating(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/admin/bookings', {
				method: 'DELETE',
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
				body: JSON.stringify({ id }),
			});
			const result = await response.json();
			if (result.success) {
				setBookings(prev => prev.filter(b => b.id !== id));
				setSelectedBooking(null);
				setShowDeleteConfirm(false);
				showToast('删除成功');
			}
		} catch {
			showToast('删除失败', 'error');
		}
		setUpdating(false);
	};

	// 日期管理
	const addBlockedDate = async (date: string, reason?: string) => {
		setUpdating(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/calendar', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
				body: JSON.stringify({ date, reason }),
			});
			const result = await response.json();
			if (result.success) {
				setBlockedDates(result.blockedDates || []);
				setSelectedCalendarDate(null);
				setBlockReason('');
				showToast('日期已关闭');
			}
		} catch {
			showToast('操作失败', 'error');
		}
		setUpdating(false);
	};

	const removeBlockedDate = async (date: string) => {
		setUpdating(true);
		try {
			const token = sessionStorage.getItem('admin_token');
			const response = await fetch('/api/calendar', {
				method: 'DELETE',
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
				body: JSON.stringify({ date }),
			});
			const result = await response.json();
			if (result.success) {
				setBlockedDates(result.blockedDates || []);
				showToast('日期已开放');
			}
		} catch {
			showToast('操作失败', 'error');
		}
		setUpdating(false);
	};

	// 统计数据
	const stats = useMemo(() => {
		const today = new Date().toISOString().split('T')[0];
		const weekStart = new Date();
		weekStart.setDate(weekStart.getDate() - weekStart.getDay());
		const weekEnd = new Date(weekStart);
		weekEnd.setDate(weekEnd.getDate() + 6);

		return {
			total: bookings.length,
			pending: bookings.filter(b => b.status === 'pending').length,
			confirmed: bookings.filter(b => b.status === 'confirmed').length,
			completed: bookings.filter(b => b.status === 'completed').length,
			cancelled: bookings.filter(b => b.status === 'cancelled').length,
			today: bookings.filter(b => b.date === today && b.status !== 'cancelled').length,
			thisWeek: bookings.filter(b => {
				const d = new Date(b.date);
				return d >= weekStart && d <= weekEnd && b.status !== 'cancelled';
			}).length,
			totalGuests: bookings.filter(b => b.status !== 'cancelled').reduce((sum, b) => sum + b.guestCount, 0),
		};
	}, [bookings]);

	// 过滤预约
	const filteredBookings = useMemo(() => {
		return bookings.filter(b => {
			const matchStatus = statusFilter === 'all' || b.status === statusFilter;
			const matchSearch = !searchQuery || 
				b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
				b.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
				b.phone.includes(searchQuery);
			return matchStatus && matchSearch;
		});
	}, [bookings, statusFilter, searchQuery]);

	// 日历工具函数
	const getDaysInMonth = (): (number | null)[] => {
		const year = calendarMonth.getFullYear();
		const month = calendarMonth.getMonth();
		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const days: (number | null)[] = [];
		for (let i = 0; i < firstDay; i++) days.push(null);
		for (let i = 1; i <= daysInMonth; i++) days.push(i);
		return days;
	};

	const formatCalendarDate = (day: number): string => {
		const year = calendarMonth.getFullYear();
		const month = String(calendarMonth.getMonth() + 1).padStart(2, '0');
		const d = String(day).padStart(2, '0');
		return `${year}-${month}-${d}`;
	};

	const isDateBlocked = (day: number): boolean => blockedDates.some(b => b.date === formatCalendarDate(day));
	const getDateBookings = (day: number): Booking[] => bookings.filter(b => b.date === formatCalendarDate(day) && b.status !== 'cancelled');
	const isPastDate = (day: number): boolean => {
		const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
		const todayDate = new Date();
		todayDate.setHours(0, 0, 0, 0);
		return date < todayDate;
	};

	// 格式化
	const formatDate = (dateStr: string) => {
		try {
			return new Date(dateStr + 'T00:00:00').toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', weekday: 'short' });
		} catch { return dateStr; }
	};

	const formatCreatedAt = (dateStr: string) => {
		try {
			const date = new Date(dateStr);
			const diffMs = Date.now() - date.getTime();
			const diffMins = Math.floor(diffMs / 60000);
			if (diffMins < 60) return `${diffMins}分钟前`;
			const diffHours = Math.floor(diffMs / 3600000);
			if (diffHours < 24) return `${diffHours}小时前`;
			const diffDays = Math.floor(diffMs / 86400000);
			if (diffDays < 7) return `${diffDays}天前`;
			return date.toLocaleDateString('zh-CN');
		} catch { return dateStr; }
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(() => showToast('已复制', 'info'));
	};

	// 状态配置
	const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
		pending: { label: '待处理', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
		confirmed: { label: '已确认', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
		completed: { label: '已完成', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
		cancelled: { label: '已取消', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
	};

	// 菜单配置
	const menuItems = [
		{ key: 'dashboard', icon: '📊', label: '仪表盘' },
		{ key: 'bookings', icon: '📋', label: '预约管理' },
		{ key: 'calendar', icon: '📅', label: '日期管理' },
		{ key: 'reviews', icon: '⭐', label: '评价管理' },
		{ key: 'coupons', icon: '🎟️', label: '优惠码' },
		{ key: 'gallery', icon: '🖼️', label: '图库管理' },
		{ key: 'instagram', icon: '📸', label: 'Instagram' },
		{ key: 'settings', icon: '⚙️', label: '设置' },
	];

	// ========== 登录页面 ==========
	if (!isAuthenticated) {
		return (
			<div className="admin-login-page">
				<SEO title="管理后台登录" />
				<div className="login-container">
					<div className="login-brand">
						<span className="brand-icon">🍱</span>
						<h1>Happy Hibachi</h1>
						<p>管理后台</p>
					</div>
					<form onSubmit={handleLogin} className="login-form">
						<div className="form-group">
							<label>密码</label>
							<input
								type="password"
								value={password}
								onChange={e => setPassword(e.target.value)}
								placeholder="请输入管理密码"
								autoFocus
							/>
						</div>
						{loginError && <div className="form-error">{loginError}</div>}
						<button type="submit" className="btn-primary" disabled={loading}>
							{loading ? '登录中...' : '登录'}
						</button>
					</form>
					<div className="login-footer">
						<p>联系支持: {CONTACT_INFO.phone}</p>
					</div>
				</div>
			</div>
		);
	}

	// ========== 主界面 ==========
	return (
		<div className="admin-layout">
			<SEO title="管理后台" />
			
			{/* Toast */}
			{toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}

			{/* 侧边栏 */}
			<aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
				<div className="sidebar-header">
					<span className="sidebar-logo">🍱</span>
					{!sidebarCollapsed && <span className="sidebar-title">Happy Hibachi</span>}
					<button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
						{sidebarCollapsed ? '→' : '←'}
					</button>
				</div>
				<nav className="sidebar-nav">
					{menuItems.map(item => (
						<button
							key={item.key}
							className={`nav-item ${activeMenu === item.key ? 'active' : ''}`}
							onClick={() => { setActiveMenu(item.key as MenuType); setSelectedBooking(null); }}
						>
							<span className="nav-icon">{item.icon}</span>
							{!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
						</button>
					))}
				</nav>
				<div className="sidebar-footer">
					<button className="nav-item logout" onClick={handleLogout}>
						<span className="nav-icon">🚪</span>
						{!sidebarCollapsed && <span className="nav-label">退出登录</span>}
					</button>
				</div>
			</aside>

			{/* 主内容区 */}
			<main className="main-content">
				{/* 顶部栏 */}
				<header className="topbar">
					<div className="topbar-left">
						<h1 className="page-title">
							{menuItems.find(m => m.key === activeMenu)?.icon} {menuItems.find(m => m.key === activeMenu)?.label}
						</h1>
					</div>
					<div className="topbar-right">
						<button className="btn-icon" onClick={fetchBookings} disabled={loading}>
							{loading ? '⏳' : '🔄'}
						</button>
						<div className="user-info">
							<span className="user-avatar">👤</span>
							<span className="user-name">管理员</span>
						</div>
					</div>
				</header>

				{/* 内容区 */}
				<div className="content-area">
					{/* ========== 仪表盘 ========== */}
					{activeMenu === 'dashboard' && (
						<div className="dashboard">
							{/* 统计卡片 */}
							<div className="stats-grid">
								<div className="stat-card highlight">
									<div className="stat-icon">📆</div>
									<div className="stat-info">
										<span className="stat-value">{stats.today}</span>
										<span className="stat-label">今日预约</span>
									</div>
								</div>
								<div className="stat-card">
									<div className="stat-icon">📊</div>
									<div className="stat-info">
										<span className="stat-value">{stats.thisWeek}</span>
										<span className="stat-label">本周预约</span>
									</div>
								</div>
								<div className="stat-card warning">
									<div className="stat-icon">⏳</div>
									<div className="stat-info">
										<span className="stat-value">{stats.pending}</span>
										<span className="stat-label">待处理</span>
									</div>
								</div>
								<div className="stat-card success">
									<div className="stat-icon">✓</div>
									<div className="stat-info">
										<span className="stat-value">{stats.confirmed}</span>
										<span className="stat-label">已确认</span>
									</div>
								</div>
								<div className="stat-card info">
									<div className="stat-icon">✓✓</div>
									<div className="stat-info">
										<span className="stat-value">{stats.completed}</span>
										<span className="stat-label">已完成</span>
									</div>
								</div>
								<div className="stat-card">
									<div className="stat-icon">👥</div>
									<div className="stat-info">
										<span className="stat-value">{stats.totalGuests}</span>
										<span className="stat-label">总客人数</span>
									</div>
								</div>
							</div>

							{/* 最近预约 */}
							<div className="card">
								<div className="card-header">
									<h2>最近预约</h2>
									<button className="btn-text" onClick={() => setActiveMenu('bookings')}>
										查看全部 →
									</button>
								</div>
								<div className="table-wrapper">
									<table className="data-table">
										<thead>
											<tr>
												<th>客户</th>
												<th>日期</th>
												<th>人数</th>
												<th>地区</th>
												<th>状态</th>
												<th>操作</th>
											</tr>
										</thead>
										<tbody>
											{bookings.slice(0, 5).map(booking => (
												<tr key={booking.id}>
													<td>
														<div className="cell-main">{booking.name}</div>
														<div className="cell-sub">{booking.phone}</div>
													</td>
													<td>{formatDate(booking.date)}</td>
													<td><strong>{booking.guestCount}</strong> 人</td>
													<td><span className="tag">{booking.region.toUpperCase()}</span></td>
													<td>
														<span className="status-badge" style={{ 
															color: statusConfig[booking.status].color,
															background: statusConfig[booking.status].bg 
														}}>
															{statusConfig[booking.status].label}
														</span>
													</td>
													<td>
														<button className="btn-sm" onClick={() => { setActiveMenu('bookings'); setSelectedBooking(booking); }}>
															查看
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}

					{/* ========== 预约管理 ========== */}
					{activeMenu === 'bookings' && !selectedBooking && (
						<div className="bookings-page">
							{/* 工具栏 */}
							<div className="toolbar">
								<div className="search-box">
									<span className="search-icon">🔍</span>
									<input
										type="text"
										placeholder="搜索客户名、手机号、邮箱..."
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
									/>
								</div>
								<div className="filter-group">
									{(['all', 'pending', 'confirmed', 'completed', 'cancelled'] as StatusType[]).map(status => (
										<button
											key={status}
											className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
											onClick={() => setStatusFilter(status)}
										>
											{status === 'all' ? '全部' : statusConfig[status].label}
											<span className="filter-count">
												{status === 'all' ? bookings.length : bookings.filter(b => b.status === status).length}
											</span>
										</button>
									))}
								</div>
							</div>

							{/* 预约列表 */}
							<div className="card">
								{loading ? (
									<div className="loading-state"><div className="spinner" /><p>加载中...</p></div>
								) : filteredBookings.length === 0 ? (
									<div className="empty-state"><span>📭</span><p>暂无预约数据</p></div>
								) : (
									<div className="table-wrapper">
										<table className="data-table">
											<thead>
												<tr>
													<th>客户信息</th>
													<th>联系方式</th>
													<th>预约日期</th>
													<th>人数</th>
													<th>地区</th>
													<th>类型</th>
													<th>状态</th>
													<th>提交时间</th>
													<th>操作</th>
												</tr>
											</thead>
											<tbody>
												{filteredBookings.map(booking => (
													<tr key={booking.id} className={booking.date === new Date().toISOString().split('T')[0] ? 'row-highlight' : ''}>
														<td>
															<div className="cell-main">{booking.name}</div>
														</td>
														<td>
															<div className="cell-main">{booking.phone}</div>
															<div className="cell-sub">{booking.email}</div>
														</td>
														<td>
															<div className="cell-main">{formatDate(booking.date)}</div>
															<div className="cell-sub">{booking.time || '待定'}</div>
														</td>
														<td><strong>{booking.guestCount}</strong> 人</td>
														<td><span className="tag">{booking.region.toUpperCase()}</span></td>
														<td>
															<span className={`type-badge ${booking.formType}`}>
																{booking.formType === 'booking' ? '预约' : '询价'}
															</span>
														</td>
														<td>
															<span className="status-badge" style={{ 
																color: statusConfig[booking.status].color,
																background: statusConfig[booking.status].bg 
															}}>
																{statusConfig[booking.status].label}
															</span>
														</td>
														<td className="cell-muted">{formatCreatedAt(booking.createdAt)}</td>
														<td>
															<button className="btn-sm" onClick={() => setSelectedBooking(booking)}>
																详情
															</button>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						</div>
					)}

					{/* ========== 预约详情 ========== */}
					{activeMenu === 'bookings' && selectedBooking && (
						<div className="booking-detail">
							{/* 删除确认 */}
							{showDeleteConfirm && (
								<div className="modal-overlay">
									<div className="modal">
										<h3>⚠️ 确认删除</h3>
										<p>确定要删除 <strong>{selectedBooking.name}</strong> 的预约吗？</p>
										<p className="text-danger">此操作不可撤销</p>
										<div className="modal-actions">
											<button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>取消</button>
											<button className="btn-danger" onClick={() => deleteBooking(selectedBooking.id)} disabled={updating}>
												{updating ? '删除中...' : '确认删除'}
											</button>
										</div>
									</div>
								</div>
							)}

							{/* 返回按钮 */}
							<button className="btn-back" onClick={() => setSelectedBooking(null)}>
								← 返回列表
							</button>

							<div className="detail-grid">
								{/* 客户信息卡片 */}
								<div className="card">
									<div className="card-header">
										<h2>客户信息</h2>
										<span className="status-badge" style={{ 
											color: statusConfig[selectedBooking.status].color,
											background: statusConfig[selectedBooking.status].bg 
										}}>
											{statusConfig[selectedBooking.status].label}
										</span>
									</div>
									<div className="detail-info">
										<div className="info-row">
											<span className="info-label">姓名</span>
											<span className="info-value">{selectedBooking.name}</span>
										</div>
										<div className="info-row">
											<span className="info-label">电话</span>
											<span className="info-value">
												{selectedBooking.phone}
												<button className="btn-icon-sm" onClick={() => copyToClipboard(selectedBooking.phone)}>📋</button>
											</span>
										</div>
										<div className="info-row">
											<span className="info-label">邮箱</span>
											<span className="info-value">
												{selectedBooking.email}
												<button className="btn-icon-sm" onClick={() => copyToClipboard(selectedBooking.email)}>📋</button>
											</span>
										</div>
									</div>
									<div className="quick-actions">
										<a href={`tel:${selectedBooking.phone}`} className="btn-action call">📞 拨打电话</a>
										<a href={`sms:${selectedBooking.phone}`} className="btn-action sms">💬 发送短信</a>
										<a href={`mailto:${selectedBooking.email}`} className="btn-action email">📧 发送邮件</a>
									</div>
								</div>

								{/* 预约详情卡片 */}
								<div className="card">
									<div className="card-header">
										<h2>预约详情</h2>
										<span className={`type-badge ${selectedBooking.formType}`}>
											{selectedBooking.formType === 'booking' ? '🎉 预约请求' : '📋 询价请求'}
										</span>
									</div>
									<div className="detail-info">
										<div className="info-row">
											<span className="info-label">预约日期</span>
											<span className="info-value highlight">{formatDate(selectedBooking.date)}</span>
										</div>
										<div className="info-row">
											<span className="info-label">预约时间</span>
											<span className="info-value">{selectedBooking.time || '待定'}</span>
										</div>
										<div className="info-row">
											<span className="info-label">人数</span>
											<span className="info-value highlight">{selectedBooking.guestCount} 人</span>
										</div>
										<div className="info-row">
											<span className="info-label">地区</span>
											<span className="info-value">{selectedBooking.region.toUpperCase()}</span>
										</div>
										<div className="info-row">
											<span className="info-label">提交时间</span>
											<span className="info-value muted">{formatCreatedAt(selectedBooking.createdAt)}</span>
										</div>
									</div>
									{selectedBooking.message && (
										<div className="message-section">
											<h4>客户备注</h4>
											<div className="message-content">{selectedBooking.message}</div>
										</div>
									)}
								</div>

								{/* 状态管理卡片 */}
								<div className="card">
									<div className="card-header">
										<h2>状态管理</h2>
									</div>
									<div className="status-actions">
										{(['pending', 'confirmed', 'completed', 'cancelled'] as const).map(status => (
											<button
												key={status}
												className={`status-btn ${selectedBooking.status === status ? 'active' : ''}`}
												style={{ 
													borderColor: statusConfig[status].color,
													background: selectedBooking.status === status ? statusConfig[status].bg : 'transparent',
													color: statusConfig[status].color
												}}
												onClick={() => updateBookingStatus(selectedBooking.id, status)}
												disabled={selectedBooking.status === status || updating}
											>
												{statusConfig[status].label}
											</button>
										))}
									</div>
								</div>

								{/* 危险操作 */}
								<div className="card danger">
									<div className="card-header">
										<h2>危险操作</h2>
									</div>
									<button className="btn-danger-outline" onClick={() => setShowDeleteConfirm(true)}>
										🗑️ 删除此预约
									</button>
								</div>
							</div>
						</div>
					)}

					{/* ========== 日期管理 ========== */}
					{activeMenu === 'calendar' && (
						<div className="calendar-page">
							<div className="calendar-grid">
								{/* 日历 */}
								<div className="card">
									<div className="card-header">
										<button className="btn-icon" onClick={() => setCalendarMonth(new Date(calendarMonth.setMonth(calendarMonth.getMonth() - 1)))}>◀</button>
										<h2>{calendarMonth.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long' })}</h2>
										<button className="btn-icon" onClick={() => setCalendarMonth(new Date(calendarMonth.setMonth(calendarMonth.getMonth() + 1)))}>▶</button>
									</div>
									<div className="calendar-widget">
										<div className="calendar-weekdays">
											{['日', '一', '二', '三', '四', '五', '六'].map(d => <div key={d} className="weekday">{d}</div>)}
										</div>
										<div className="calendar-days">
											{getDaysInMonth().map((day, i) => {
												if (day === null) return <div key={`e-${i}`} className="day empty" />;
												const dateStr = formatCalendarDate(day);
												const isBlocked = isDateBlocked(day);
												const dayBookings = getDateBookings(day);
												const isPast = isPastDate(day);
												const isSelected = selectedCalendarDate === dateStr;
												return (
													<button
														key={day}
														className={`day ${isPast ? 'past' : ''} ${isBlocked ? 'blocked' : ''} ${isSelected ? 'selected' : ''}`}
														onClick={() => !isPast && setSelectedCalendarDate(isSelected ? null : dateStr)}
														disabled={isPast}
													>
														<span className="day-num">{day}</span>
														{dayBookings.length > 0 && <span className="day-count">{dayBookings.length}</span>}
														{isBlocked && <span className="day-blocked">🚫</span>}
													</button>
												);
											})}
										</div>
									</div>
									<div className="calendar-legend">
										<div className="legend-item"><span className="legend-dot available" /> 可预约</div>
										<div className="legend-item"><span className="legend-dot blocked" /> 已关闭</div>
										<div className="legend-item"><span className="legend-dot booked" /> 有预约</div>
									</div>
								</div>

								{/* 日期详情 */}
								<div className="card">
									{selectedCalendarDate ? (
										<>
											<div className="card-header">
												<h2>{formatDate(selectedCalendarDate)}</h2>
											</div>
											{isDateBlocked(parseInt(selectedCalendarDate.split('-')[2])) ? (
												<div className="date-action">
													<p className="status-text blocked">🚫 此日期已关闭</p>
													<button className="btn-success" onClick={() => removeBlockedDate(selectedCalendarDate)} disabled={updating}>
														{updating ? '处理中...' : '开放此日期'}
													</button>
												</div>
											) : (
												<div className="date-action">
													<p className="status-text open">✓ 此日期可预约</p>
													<input
														type="text"
														placeholder="关闭原因（可选）"
														value={blockReason}
														onChange={e => setBlockReason(e.target.value)}
														className="input"
													/>
													<button className="btn-danger" onClick={() => addBlockedDate(selectedCalendarDate, blockReason)} disabled={updating}>
														{updating ? '处理中...' : '关闭此日期'}
													</button>
												</div>
											)}
											{getDateBookings(parseInt(selectedCalendarDate.split('-')[2])).length > 0 && (
												<div className="date-bookings">
													<h4>当天预约</h4>
													{getDateBookings(parseInt(selectedCalendarDate.split('-')[2])).map(b => (
														<div key={b.id} className="mini-booking" onClick={() => { setActiveMenu('bookings'); setSelectedBooking(b); }}>
															<span>{b.name}</span>
															<span>{b.guestCount}人</span>
															<span style={{ color: statusConfig[b.status].color }}>{statusConfig[b.status].label}</span>
														</div>
													))}
												</div>
											)}
										</>
									) : (
										<div className="empty-state"><p>选择日期查看详情</p></div>
									)}
								</div>
							</div>

							{/* 已关闭日期列表 */}
							<div className="card">
								<div className="card-header">
									<h2>🚫 已关闭的日期 ({blockedDates.length})</h2>
								</div>
								{blockedDates.length === 0 ? (
									<div className="empty-state"><p>暂无关闭的日期</p></div>
								) : (
									<div className="blocked-list">
										{blockedDates.sort((a, b) => a.date.localeCompare(b.date)).map(blocked => (
											<div key={blocked.date} className="blocked-item">
												<div className="blocked-info">
													<span className="blocked-date">{formatDate(blocked.date)}</span>
													{blocked.reason && <span className="blocked-reason">{blocked.reason}</span>}
												</div>
												<button className="btn-sm-success" onClick={() => removeBlockedDate(blocked.date)}>开放</button>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* ========== 评价管理 ========== */}
					{activeMenu === 'reviews' && (
						<div className="reviews-page">
							{/* 添加评价 */}
							<div className="card">
								<div className="card-header"><h2>➕ 添加新评价</h2></div>
								<form
									className="add-review-form"
									onSubmit={async (e) => {
										e.preventDefault();
										const form = e.target as HTMLFormElement;
										const formData = new FormData(form);
										const success = await addReview({
											name: formData.get('name') as string,
											location: formData.get('location') as string,
											rating: parseInt(formData.get('rating') as string) || 5,
											review: formData.get('review') as string,
											event: formData.get('event') as string,
											visible: true,
										});
										if (success) form.reset();
									}}
								>
									<div className="form-row-2">
										<div className="form-group">
											<label>客户姓名 *</label>
											<input name="name" required placeholder="如：Sarah M." />
										</div>
										<div className="form-group">
											<label>地区</label>
											<input name="location" placeholder="如：Los Angeles, CA" />
										</div>
									</div>
									<div className="form-row-2">
										<div className="form-group">
											<label>评分 *</label>
											<select name="rating" defaultValue="5">
												<option value="5">⭐⭐⭐⭐⭐ (5星)</option>
												<option value="4">⭐⭐⭐⭐ (4星)</option>
												<option value="3">⭐⭐⭐ (3星)</option>
												<option value="2">⭐⭐ (2星)</option>
												<option value="1">⭐ (1星)</option>
											</select>
										</div>
										<div className="form-group">
											<label>活动类型</label>
											<input name="event" placeholder="如：Birthday Party" />
										</div>
									</div>
									<div className="form-group">
										<label>评价内容 *</label>
										<textarea name="review" required rows={3} placeholder="客户的评价内容..." />
									</div>
									<button type="submit" className="btn-primary">添加评价</button>
								</form>
							</div>

							{/* 评价列表 */}
							<div className="card">
								<div className="card-header">
									<h2>📝 评价列表 ({reviews.length})</h2>
								</div>
								{reviews.length === 0 ? (
									<div className="empty-state">
										<span>⭐</span>
										<p>暂无评价</p>
									</div>
								) : (
									<div className="reviews-list">
										{reviews.map(review => (
											<div key={review.id} className={`review-item ${!review.visible ? 'hidden' : ''}`}>
												<div className="review-header">
													<div className="review-info">
														<span className="review-name">{review.name}</span>
														{review.location && <span className="review-location">{review.location}</span>}
													</div>
													<div className="review-rating">
														{'⭐'.repeat(review.rating)}
													</div>
												</div>
												<p className="review-text">{review.review}</p>
												<div className="review-footer">
													{review.event && <span className="review-event">📌 {review.event}</span>}
													<span className="review-date">
														{new Date(review.createdAt).toLocaleDateString()}
													</span>
												</div>
												<div className="review-actions">
													<button
														className={`btn-sm ${review.visible ? '' : 'btn-sm-success'}`}
														onClick={() => toggleReviewVisibility(review.id, !review.visible)}
													>
														{review.visible ? '👁️ 隐藏' : '👁️ 显示'}
													</button>
													<button
														className="btn-sm"
														onClick={() => deleteReview(review.id)}
														style={{ color: 'var(--admin-danger)' }}
													>
														🗑️ 删除
													</button>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* ========== 优惠码管理 ========== */}
					{activeMenu === 'coupons' && (
						<div className="coupons-page">
							{/* 创建优惠码 */}
							<div className="card">
								<div className="card-header">
									<h2>🎟️ 优惠码管理</h2>
									<button className="btn-primary" onClick={() => setShowCouponForm(!showCouponForm)}>
										{showCouponForm ? '取消' : '+ 新建优惠码'}
									</button>
								</div>

								{showCouponForm && (
									<div className="coupon-form">
										<div className="form-row-2">
											<div className="form-group">
												<label>优惠码 *</label>
												<input
													type="text"
													value={newCoupon.code || ''}
													onChange={(e) => setNewCoupon(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
													placeholder="如：HAPPY10"
													style={{ textTransform: 'uppercase' }}
												/>
											</div>
											<div className="form-group">
												<label>折扣类型</label>
												<select
													value={newCoupon.type}
													onChange={(e) => setNewCoupon(prev => ({ ...prev, type: e.target.value as 'percentage' | 'fixed' }))}
												>
													<option value="percentage">百分比折扣</option>
													<option value="fixed">固定金额</option>
												</select>
											</div>
										</div>
										<div className="form-row-2">
											<div className="form-group">
												<label>折扣值 *</label>
												<div className="input-with-suffix">
													<input
														type="number"
														value={newCoupon.value || 0}
														onChange={(e) => setNewCoupon(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
														min="0"
													/>
													<span>{newCoupon.type === 'percentage' ? '%' : '$'}</span>
												</div>
											</div>
											<div className="form-group">
												<label>最低人数</label>
												<input
													type="number"
													value={newCoupon.minGuests || 0}
													onChange={(e) => setNewCoupon(prev => ({ ...prev, minGuests: parseInt(e.target.value) || 0 }))}
													min="0"
													placeholder="0 = 无限制"
												/>
											</div>
										</div>
										<div className="form-row-2">
											<div className="form-group">
												<label>最大使用次数</label>
												<input
													type="number"
													value={newCoupon.maxUses || 0}
													onChange={(e) => setNewCoupon(prev => ({ ...prev, maxUses: parseInt(e.target.value) || 0 }))}
													min="0"
													placeholder="0 = 无限制"
												/>
											</div>
											<div className="form-group">
												<label>有效期</label>
												<div className="date-range">
													<input
														type="date"
														value={newCoupon.validFrom || ''}
														onChange={(e) => setNewCoupon(prev => ({ ...prev, validFrom: e.target.value }))}
													/>
													<span>至</span>
													<input
														type="date"
														value={newCoupon.validUntil || ''}
														onChange={(e) => setNewCoupon(prev => ({ ...prev, validUntil: e.target.value }))}
													/>
												</div>
											</div>
										</div>
										<button className="btn-primary" onClick={createCoupon}>
											创建优惠码
										</button>
									</div>
								)}

								{/* 优惠码列表 */}
								{coupons.length === 0 ? (
									<div className="empty-state">
										<span>🎟️</span>
										<p>暂无优惠码</p>
									</div>
								) : (
									<div className="coupons-list">
										{coupons.map(coupon => {
											const isExpired = new Date(coupon.validUntil + 'T23:59:59') < new Date();
											const isMaxed = coupon.maxUses > 0 && coupon.usedCount >= coupon.maxUses;

											return (
												<div key={coupon.id} className={`coupon-item ${!coupon.enabled || isExpired || isMaxed ? 'disabled' : ''}`}>
													<div className="coupon-main">
														<div className="coupon-code">{coupon.code}</div>
														<div className="coupon-value">
															{coupon.type === 'percentage' ? `${coupon.value}% OFF` : `$${coupon.value} OFF`}
														</div>
													</div>
													<div className="coupon-details">
														<span>👥 最低 {coupon.minGuests || '不限'} 人</span>
														<span>🎯 已用 {coupon.usedCount}/{coupon.maxUses || '∞'}</span>
														<span>📅 {coupon.validFrom} ~ {coupon.validUntil}</span>
													</div>
													<div className="coupon-status">
														{isExpired && <span className="status-badge expired">已过期</span>}
														{isMaxed && <span className="status-badge maxed">已用完</span>}
														{!isExpired && !isMaxed && (
															<span className={`status-badge ${coupon.enabled ? 'active' : 'inactive'}`}>
																{coupon.enabled ? '启用中' : '已停用'}
															</span>
														)}
													</div>
													<div className="coupon-actions">
														<button
															className={`btn-sm ${coupon.enabled ? '' : 'btn-sm-success'}`}
															onClick={() => toggleCoupon(coupon.id, !coupon.enabled)}
															disabled={isExpired || isMaxed}
														>
															{coupon.enabled ? '停用' : '启用'}
														</button>
														<button
															className="btn-sm"
															onClick={() => deleteCoupon(coupon.id)}
															style={{ color: 'var(--admin-danger)' }}
														>
															删除
														</button>
													</div>
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* 推荐来源统计 */}
							<div className="card">
								<div className="card-header">
									<h2>📊 推荐来源统计</h2>
								</div>
								{referralStats.length === 0 ? (
									<div className="empty-state">
										<span>📊</span>
										<p>暂无数据</p>
									</div>
								) : (
									<div className="referral-stats">
										{referralStats.map(([source, count], index) => (
											<div key={source} className="referral-item">
												<span className="referral-rank">#{index + 1}</span>
												<span className="referral-source">{source}</span>
												<span className="referral-count">{count} 次预约</span>
												<div
													className="referral-bar"
													style={{ width: `${(count / referralStats[0][1]) * 100}%` }}
												/>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* ========== 图库管理 ========== */}
					{activeMenu === 'gallery' && (
						<div className="gallery-page">
							<div className="card">
								<div className="card-header">
									<h2>🖼️ 图库管理</h2>
									<label className="btn-sm-success" style={{ cursor: 'pointer' }}>
										+ 上传图片
										<input
											type="file"
											accept="image/*"
											multiple
											style={{ display: 'none' }}
											onChange={handleGalleryUpload}
										/>
									</label>
								</div>
								<div className="gallery-grid">
									{(settings.galleryImages || []).length === 0 ? (
										<div className="empty-state">
											<span>📷</span>
											<p>暂无图片，点击上方按钮上传</p>
										</div>
									) : (
										(settings.galleryImages || [])
											.sort((a, b) => a.order - b.order)
											.map((img, idx) => (
												<div key={img.id} className="gallery-item">
													<img src={img.url} alt={img.caption || `Gallery ${idx + 1}`} />
													<div className="gallery-item-overlay">
														<input
															type="text"
															className="caption-input"
															placeholder="添加说明..."
															value={img.caption}
															onChange={(e) => updateGalleryCaption(img.id, e.target.value)}
															onBlur={() => saveSettings({ galleryImages: settings.galleryImages })}
														/>
														<div className="gallery-item-actions">
															<button
																className="btn-icon-sm"
																onClick={() => moveGalleryImage(img.id, 'up')}
																disabled={idx === 0}
																title="上移"
															>
																⬆️
															</button>
															<button
																className="btn-icon-sm"
																onClick={() => moveGalleryImage(img.id, 'down')}
																disabled={idx === (settings.galleryImages || []).length - 1}
																title="下移"
															>
																⬇️
															</button>
															<button
																className="btn-icon-sm danger"
																onClick={() => deleteGalleryImage(img.id)}
																title="删除"
															>
																🗑️
															</button>
														</div>
													</div>
												</div>
											))
									)}
								</div>
								<p style={{ 
									fontSize: '12px', 
									color: 'var(--admin-text-muted)', 
									padding: '16px 20px',
									margin: 0,
									borderTop: '1px solid var(--admin-border)'
								}}>
									建议图片尺寸: 800x600 或更大，支持 JPG/PNG 格式
								</p>
							</div>
						</div>
					)}

					{/* ========== Instagram 管理 ========== */}
					{activeMenu === 'instagram' && (
						<div className="instagram-page">
							{/* Instagram 账号设置 */}
							<div className="card">
								<div className="card-header">
									<h2>📸 Instagram 账号</h2>
								</div>
								<div className="detail-info">
									<div className="info-row">
										<span className="info-label">账号</span>
										<div className="input-with-prefix">
											<span className="input-prefix">@</span>
											<input
												type="text"
												value={instagramSettings.handle}
												onChange={e => setInstagramSettings(prev => ({ ...prev, handle: e.target.value.replace('@', '') }))}
												onBlur={e => saveInstagramHandle(e.target.value.replace('@', ''))}
												placeholder="happyhibachi"
												className="input"
											/>
										</div>
									</div>
								</div>
							</div>

							{/* 添加新帖子 */}
							<div className="card">
								<div className="card-header">
									<h2>➕ 添加新帖子</h2>
								</div>
								<div className="add-post-form">
									<div className="add-post-upload">
										{newPostImage ? (
											<div className="upload-preview">
												<img src={newPostImage} alt="预览" />
												<button className="upload-remove" onClick={() => setNewPostImage(null)}>✕</button>
											</div>
										) : (
											<label className="upload-area">
												<input
													type="file"
													accept="image/*"
													onChange={handleImageUpload}
													hidden
												/>
												<span className="upload-icon">📷</span>
												<span className="upload-text">点击上传图片</span>
												<span className="upload-hint">最大 2MB</span>
											</label>
										)}
									</div>
									<div className="add-post-fields">
										<div className="form-group">
											<label>Instagram 链接</label>
											<input
												type="text"
												value={newPostLink}
												onChange={e => setNewPostLink(e.target.value)}
												placeholder="https://www.instagram.com/p/ABC123/"
												className="input"
											/>
										</div>
										<button
											className="btn-primary"
											onClick={addInstagramPost}
											disabled={instagramLoading || !newPostImage || !newPostLink.trim()}
										>
											{instagramLoading ? '添加中...' : '添加帖子'}
										</button>
									</div>
								</div>
							</div>

							{/* 帖子列表 */}
							<div className="card">
								<div className="card-header">
									<h2>📝 帖子列表</h2>
									<span className="card-badge">{instagramSettings.posts.length} 个帖子</span>
								</div>
								{instagramSettings.posts.length === 0 ? (
									<div className="empty-state">
										<span>📸</span>
										<p>暂无帖子，请添加</p>
									</div>
								) : (
									<div className="instagram-posts-list">
										{instagramSettings.posts.map((post, index) => (
											<div key={post.id} className="instagram-post-item">
												<div className="post-order">
													<button
														className="btn-icon-sm"
														onClick={() => moveInstagramPost(post.id, 'up')}
														disabled={index === 0}
													>
														↑
													</button>
													<span>{index + 1}</span>
													<button
														className="btn-icon-sm"
														onClick={() => moveInstagramPost(post.id, 'down')}
														disabled={index === instagramSettings.posts.length - 1}
													>
														↓
													</button>
												</div>
												<div className="post-image">
													<img src={post.image} alt={`帖子 ${index + 1}`} />
												</div>
												<div className="post-info">
													<a href={post.link} target="_blank" rel="noopener noreferrer">
														{post.link.length > 40 ? post.link.slice(0, 40) + '...' : post.link}
													</a>
												</div>
												<button
													className="btn-icon-sm danger"
													onClick={() => deleteInstagramPost(post.id)}
													title="删除"
												>
													🗑️
												</button>
											</div>
										))}
									</div>
								)}
							</div>

							{/* 使用说明 */}
							<div className="card">
								<div className="card-header">
									<h2>📖 使用说明</h2>
								</div>
								<div className="help-content" style={{ padding: '16px 20px' }}>
									<ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
										<li>在 Instagram 打开要展示的帖子，<strong>截图</strong>或<strong>保存图片</strong></li>
										<li>点击「复制链接」获取帖子 URL</li>
										<li>上传图片并粘贴链接，点击「添加帖子」</li>
										<li>可使用 ↑↓ 按钮调整显示顺序</li>
										<li>建议添加 <strong>6 个帖子</strong>，移动端会自动隐藏后 2 个</li>
									</ol>
								</div>
							</div>
						</div>
					)}

					{/* ========== 设置 ========== */}
					{activeMenu === 'settings' && (
						<div className="settings-page">
							{/* 时间段设置 */}
							<div className="card">
								<div className="card-header">
									<h2>⏰ 可预约时间段</h2>
									<button className="btn-sm-success" onClick={addTimeSlot}>+ 添加时段</button>
								</div>
								<div className="time-slots-list">
									{settings.timeSlots.map(slot => (
										<div key={slot.id} className={`time-slot-item ${!slot.enabled ? 'disabled' : ''}`}>
											<div className="slot-main">
												<label className="toggle-switch">
													<input
														type="checkbox"
														checked={slot.enabled}
														onChange={() => toggleTimeSlot(slot.id)}
													/>
													<span className="toggle-slider"></span>
												</label>
												<input
													type="text"
													className="slot-label-input"
													value={slot.label}
													onChange={(e) => updateTimeSlotLabel(slot.id, e.target.value)}
													onBlur={() => saveSettings({ timeSlots: settings.timeSlots })}
													placeholder="时段名称"
												/>
											</div>
											<div className="slot-times">
												<input
													type="time"
													value={slot.startTime}
													onChange={(e) => updateTimeSlotTime(slot.id, 'startTime', e.target.value)}
													onBlur={() => saveSettings({ timeSlots: settings.timeSlots })}
												/>
												<span>—</span>
												<input
													type="time"
													value={slot.endTime}
													onChange={(e) => updateTimeSlotTime(slot.id, 'endTime', e.target.value)}
													onBlur={() => saveSettings({ timeSlots: settings.timeSlots })}
												/>
											</div>
											<button 
												className="btn-icon-sm"
												onClick={() => deleteTimeSlot(slot.id)}
												title="删除"
											>
												🗑️
											</button>
										</div>
									))}
								</div>
							</div>

							{/* 自动提醒 */}
							<div className="card">
								<div className="card-header">
									<h2>📧 活动提醒邮件</h2>
								</div>
								<div className="detail-info">
									<div className="info-row">
										<span className="info-label">明天的活动</span>
										<span className="info-value">
											{reminderStatus.pending > 0 ? (
												<span style={{ color: 'var(--admin-warning)' }}>
													{reminderStatus.pending} 个待提醒
												</span>
											) : (
												<span style={{ color: 'var(--admin-text-muted)' }}>无</span>
											)}
										</span>
									</div>
									<div className="info-row">
										<span className="info-label">已发送提醒</span>
										<span className="info-value">{reminderStatus.reminded} 个</span>
									</div>
								</div>
								<div style={{ padding: '16px 20px', borderTop: '1px solid var(--admin-border)' }}>
									<button
										className="btn-primary"
										onClick={sendReminders}
										disabled={reminderStatus.loading || reminderStatus.pending === 0}
										style={{ width: '100%' }}
									>
										{reminderStatus.loading ? '发送中...' : `发送提醒邮件 (${reminderStatus.pending})`}
									</button>
									<p style={{ 
										fontSize: '12px', 
										color: 'var(--admin-text-muted)', 
										marginTop: '12px',
										marginBottom: 0,
										textAlign: 'center' 
									}}>
										提醒邮件会发送给明天有活动的已确认客户
									</p>
								</div>
							</div>

							{/* 促销横幅 */}
							<div className="card">
								<div className="card-header">
									<h2>📢 促销横幅</h2>
									<label className="toggle-switch">
										<input
											type="checkbox"
											checked={settings.promoBanner?.enabled ?? true}
											onChange={(e) => saveSettings({
												promoBanner: { ...settings.promoBanner, enabled: e.target.checked }
											})}
										/>
										<span className="toggle-slider"></span>
									</label>
								</div>
								<div className="form-group">
									<label>Emoji</label>
									<input
										type="text"
										className="input"
										value={settings.promoBanner?.emoji ?? '🔥'}
										onChange={(e) => setSettings(s => ({
											...s,
											promoBanner: { ...s.promoBanner, emoji: e.target.value }
										}))}
										onBlur={() => saveSettings({ promoBanner: settings.promoBanner })}
										placeholder="🔥"
										style={{ width: '60px', textAlign: 'center' }}
									/>
								</div>
								<div className="form-group">
									<label>促销文案</label>
									<input
										type="text"
										className="input"
										value={settings.promoBanner?.text ?? 'Book Today & Get $30 OFF!'}
										onChange={(e) => setSettings(s => ({
											...s,
											promoBanner: { ...s.promoBanner, text: e.target.value }
										}))}
										onBlur={() => saveSettings({ promoBanner: settings.promoBanner })}
										placeholder="Book Today & Get $30 OFF!"
									/>
								</div>
							</div>

							{/* 联系方式 */}
							<div className="card">
								<div className="card-header">
									<h2>📞 联系方式</h2>
								</div>
								<div className="form-group">
									<label>联系人名称</label>
									<input
										type="text"
										className="input"
										value={settings.contactInfo?.contactPerson ?? 'Happy Hibachi Team'}
										onChange={(e) => setSettings(s => ({
											...s,
											contactInfo: { ...s.contactInfo, contactPerson: e.target.value }
										}))}
										onBlur={() => saveSettings({ contactInfo: settings.contactInfo })}
										placeholder="Happy Hibachi Team"
									/>
								</div>
								<div className="form-group">
									<label>📱 联系电话</label>
									<input
										type="tel"
										className="input"
										value={settings.contactInfo?.phone ?? ''}
										onChange={(e) => setSettings(s => ({
											...s,
											contactInfo: { ...s.contactInfo, phone: e.target.value }
										}))}
										onBlur={() => saveSettings({ contactInfo: settings.contactInfo })}
										placeholder="909-615-6633"
									/>
								</div>
								<div className="form-group">
									<label>📧 联系邮箱</label>
									<input
										type="email"
										className="input"
										value={settings.contactInfo?.email ?? ''}
										onChange={(e) => setSettings(s => ({
											...s,
											contactInfo: { ...s.contactInfo, email: e.target.value }
										}))}
										onBlur={() => saveSettings({ contactInfo: settings.contactInfo })}
										placeholder="happyhibachi@gmail.com"
									/>
								</div>
							</div>

							{/* 功能开关 */}
							<div className="card">
								<div className="card-header">
									<h2>🎛️ 功能开关</h2>
								</div>
								<div className="feature-toggles">
									{[
										{ key: 'photoShare', label: '📸 照片分享活动', desc: '用户分享照片获得折扣' },
										{ key: 'referralProgram', label: '🎁 推荐计划', desc: '推荐好友获得奖励' },
										{ key: 'newsletter', label: '📧 新闻订阅', desc: '邮件订阅功能' },
										{ key: 'specialOffer', label: '🎉 特别优惠弹窗', desc: '首次访问弹窗优惠' },
										{ key: 'instagramFeed', label: '📷 Instagram 动态', desc: '展示 Instagram 帖子' },
										{ key: 'coupons', label: '🎫 优惠券系统', desc: '预订时使用优惠券' },
									].map(feature => (
										<div key={feature.key} className="feature-toggle-item">
											<div className="feature-info">
												<span className="feature-label">{feature.label}</span>
												<span className="feature-desc">{feature.desc}</span>
											</div>
											<label className="toggle-switch">
												<input
													type="checkbox"
													checked={settings.featureToggles?.[feature.key as keyof FeatureToggles] ?? true}
													onChange={(e) => {
														const newToggles = {
															...DEFAULT_FEATURE_TOGGLES,
															...settings.featureToggles,
															[feature.key]: e.target.checked
														};
														setSettings(s => ({ ...s, featureToggles: newToggles }));
														saveSettings({ featureToggles: newToggles });
													}}
												/>
												<span className="toggle-slider"></span>
											</label>
										</div>
									))}
								</div>
							</div>

							{/* 社交媒体链接 */}
							<div className="card">
								<div className="card-header">
									<h2>🔗 社交媒体链接</h2>
								</div>
								<div className="form-group">
									<label>📷 Instagram</label>
									<input
										type="url"
										className="input"
										value={settings.socialLinks?.instagram ?? ''}
										onChange={(e) => setSettings(s => ({
											...s,
											socialLinks: { ...s.socialLinks, instagram: e.target.value }
										}))}
										onBlur={() => saveSettings({ socialLinks: settings.socialLinks })}
										placeholder="https://instagram.com/happyhibachi"
									/>
								</div>
								<div className="form-group">
									<label>📘 Facebook</label>
									<input
										type="url"
										className="input"
										value={settings.socialLinks?.facebook ?? ''}
										onChange={(e) => setSettings(s => ({
											...s,
											socialLinks: { ...s.socialLinks, facebook: e.target.value }
										}))}
										onBlur={() => saveSettings({ socialLinks: settings.socialLinks })}
										placeholder="https://facebook.com/happyhibachi"
									/>
								</div>
								<div className="form-group">
									<label>🎵 TikTok</label>
									<input
										type="url"
										className="input"
										value={settings.socialLinks?.tiktok ?? ''}
										onChange={(e) => setSettings(s => ({
											...s,
											socialLinks: { ...s.socialLinks, tiktok: e.target.value }
										}))}
										onBlur={() => saveSettings({ socialLinks: settings.socialLinks })}
										placeholder="https://tiktok.com/@happyhibachi"
									/>
								</div>
							</div>

							{/* 数据导出 */}
							<div className="card">
								<div className="card-header">
									<h2>📥 数据导出</h2>
								</div>
								<div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
									<button
										className="btn-primary"
										onClick={exportBookingsCSV}
										style={{ width: '100%' }}
									>
										📄 导出 CSV
									</button>
									<button
										className="btn-secondary"
										onClick={copyBookingsToClipboard}
										style={{ width: '100%' }}
									>
										📋 复制全部数据
									</button>
									<button
										className="btn-secondary"
										onClick={exportBookingsAsImage}
										style={{ width: '100%' }}
									>
										🖼️ 保存为图片
									</button>
									<p style={{ 
										fontSize: '12px', 
										color: 'var(--admin-text-muted)', 
										marginTop: '4px',
										marginBottom: 0,
										textAlign: 'center' 
									}}>
										共 {bookings.length} 条预约记录
									</p>
								</div>
							</div>

							{/* 系统信息 */}
							<div className="card">
								<div className="card-header"><h2>📊 系统信息</h2></div>
								<div className="detail-info">
									<div className="info-row">
										<span className="info-label">版本</span>
										<span className="info-value">1.0.0</span>
									</div>
									<div className="info-row">
										<span className="info-label">总预约数</span>
										<span className="info-value">{stats.total}</span>
									</div>
									<div className="info-row">
										<span className="info-label">已关闭日期</span>
										<span className="info-value">{blockedDates.length}</span>
									</div>
									<div className="info-row">
										<span className="info-label">启用时段</span>
										<span className="info-value">{settings.timeSlots.filter(s => s.enabled).length} / {settings.timeSlots.length}</span>
									</div>
								</div>
							</div>

							{/* 联系方式 */}
							<div className="card">
								<div className="card-header"><h2>📞 联系方式</h2></div>
								<div className="detail-info">
									<div className="info-row">
										<span className="info-label">电话</span>
										<span className="info-value">{CONTACT_INFO.phone}</span>
									</div>
									<div className="info-row">
										<span className="info-label">邮箱</span>
										<span className="info-value">{CONTACT_INFO.email}</span>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</main>

			{/* 移动端底部导航 */}
			<nav className="mobile-nav">
				{menuItems.map(item => (
					<button
						key={item.key}
						className={`mobile-nav-item ${activeMenu === item.key ? 'active' : ''}`}
						onClick={() => { setActiveMenu(item.key as MenuType); setSelectedBooking(null); }}
					>
						<span className="mobile-nav-icon">{item.icon}</span>
						<span className="mobile-nav-label">{item.label}</span>
					</button>
				))}
			</nav>
		</div>
	);
};

export default AdminDashboard;
